<template>
  <SidebarComponent />
  <div class="dashboard-layout">
    <div class="content-container">
      <div class="card-container">
        <div class="titolo-card">
          <h1>Invii da modificare</h1>
          <p class="page-help-text">
            Qui trovi gli utenti per cui il campo <strong>Note</strong> non risulta ancora <strong>Corretta</strong>
            oppure la voce <strong>Consegna programmi</strong> non è ancora segnata come consegnata. Una volta
            completati entrambi gli step, la riga si nasconde automaticamente.
            <strong class="page-help-text__warning">
              Dopo aver impostato Note su Corretta, ricordati di salvare.
              <span class="material-icons page-help-text__warning-icon" aria-hidden="true">save</span>
            </strong>
          </p>
        </div>

        <section class="toolbar-card card-surface">
          <div class="filters-header">
            <button
              type="button"
              class="accordion-toggle"
              :aria-expanded="filtersOpen"
              @click="filtersOpen = !filtersOpen"
            >
              <div class="toggle-copy">
                <span class="toggle-title">Filtri e azioni</span>
                <span class="toggle-subtitle">Consegna, studio, email</span>
              </div>
              <span class="material-icons" aria-hidden="true">
                {{ filtersOpen ? "expand_less" : "expand_more" }}
              </span>
            </button>
          </div>

          <transition name="accordion">
            <div v-show="filtersOpen" class="accordion-panel">
              <div class="toolbar">
                <div class="toolbar-group toolbar-group--sm">
                  <label class="toolbar-label" for="invii-mod-from">Dal</label>
                  <input
                    id="invii-mod-from"
                    type="date"
                    v-model="filters.deliveryStartDate"
                    class="input date-range-input date-range-input--short"
                  />
                </div>

                <div class="toolbar-group toolbar-group--sm">
                  <label class="toolbar-label" for="invii-mod-to">Al</label>
                  <input
                    id="invii-mod-to"
                    type="date"
                    v-model="filters.deliveryEndDate"
                    class="input date-range-input date-range-input--short"
                  />
                </div>

                <div class="toolbar-group toolbar-group--md">
                  <label class="toolbar-label" for="invii-mod-studio">Studio</label>
                  <select id="invii-mod-studio" v-model="filters.studio" class="input">
                    <option value="">Tutti</option>
                    <option v-for="studio in studioOptions" :key="studio" :value="studio">
                      {{ studio }}
                    </option>
                  </select>
                </div>

                <div class="toolbar-group toolbar-group--lg">
                  <label class="toolbar-label" for="invii-mod-email">Email</label>
                  <input
                    id="invii-mod-email"
                    type="search"
                    v-model.trim="filters.email"
                    class="input input--compact"
                    placeholder="Cerca email"
                  />
                </div>

                <div class="toolbar-group toolbar-group--status">
                  <span class="toolbar-label">Stato</span>
                  <div class="status-filter-buttons" role="group" aria-label="Filtro stato invio">
                    <button
                      type="button"
                      class="status-filter-button status-filter-button--delivery"
                      :class="{ 'is-active': filters.status === 'toDeliver' }"
                      :aria-pressed="filters.status === 'toDeliver'"
                      :disabled="isLoading"
                      @click="setStatusFilter('toDeliver')"
                    >
                      Da consegnare
                    </button>
                    <button
                      type="button"
                      class="status-filter-button status-filter-button--edit"
                      :class="{ 'is-active': filters.status === 'toModify' }"
                      :aria-pressed="filters.status === 'toModify'"
                      :disabled="isLoading"
                      @click="setStatusFilter('toModify')"
                    >
                      Da modificare
                    </button>
                  </div>
                </div>

                <div class="toolbar-actions">
                  <button type="button" class="btn primary staff-button-neutral" :disabled="isLoading" @click="reloadRows">
                    {{ isLoading ? "Caricamento..." : "Cerca" }}
                  </button>
                  <button type="button" class="btn ghost" :disabled="isLoading" @click="resetFilters">
                    Reset
                  </button>
                  <button type="button" class="btn ghost btn-icon" :disabled="isLoading" title="Aggiorna" @click="reloadRows">
                    <span class="material-icons" aria-hidden="true">
                      {{ isLoading ? "autorenew" : "refresh" }}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </transition>
        </section>

        <section v-if="errorMessage" class="error-box">
          {{ errorMessage }}
        </section>

        <section v-if="successMessage" class="success-box">
          {{ successMessage }}
        </section>

        <div v-if="isLoading" class="loader">
          <h4>Caricamento in corso...</h4>
        </div>

        <div v-else class="dataset-summary">
          <span>
            Mostrando {{ filteredRows.length }} invii in lavorazione
            <template v-if="rows.length !== filteredRows.length">
              su {{ rows.length }} caricati
            </template>
          </span>
          <span v-if="inspectedRowsCount" class="muted">
            {{ inspectedRowsCount }} invii letti
          </span>
        </div>

        <div v-if="!isLoading && filteredRows.length > 0" class="tickets">
          <table class="users-table">
            <thead>
              <tr>
                <th>Azione</th>
                <th>Consegna</th>
                <th>Studio</th>
                <th>Utente</th>
                <th>Email</th>
                <th>Programma</th>
                <th>Allenamento</th>
                <th>Kcal</th>
                <th>Strategia</th>
                <th>Integrazione</th>
                <th class="col-documenti">Documenti</th>
                <th class="col-consegna">Consegna programmi</th>
                <th>Nutr. dieta</th>
                <th>Note</th>
                <th>Testo</th>
                <th>Salva</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in filteredRows"
                :key="row.id"
                :class="[
                  `row-color-${row.rowColor || 'none'}`,
                  {
                    'row-saving': isRowSaving(row.id),
                    'row-delivered-pending-removal': isRowPendingRemoval(row.id),
                  }
                ]"
              >
                <td>
                  <div class="buttons-container">
                    <a
                      class="table-icon-button"
                      :href="getUserDetailsLink(row.uid || row.id)"
                      target="_blank"
                      rel="noopener"
                      title="Apri anagrafica"
                    >
                      <span class="material-icons" aria-hidden="true">visibility</span>
                    </a>
                  </div>
                </td>
                <td>{{ formatDate(row.dataConsegna) }}</td>
                <td>{{ row.studio || "-" }}</td>
                <td>
                  <strong>{{ fullName(row) }}</strong>
                  <small>{{ row.phone || "-" }}</small>
                </td>
                <td class="mono">{{ row.email || "-" }}</td>
                <td>
                  <div class="mono" style="font-weight: 600;">
                    {{ formatDate(row.latestProgram.dataCreazione) }}
                  </div>
                  <small class="muted">{{ row.latestProgram.percorsoTitle || row.latestProgram.id || "-" }}</small>
                </td>
                <td>{{ row.latestProgram.allenamento || "-" }}</td>
                <td>{{ row.latestProgram.kcalalimentazioni || "-" }}</td>
                <td>{{ row.latestProgram.strategia || "-" }}</td>
                <td>{{ row.latestProgram.pianoIntegrazione || "-" }}</td>
                <td class="col-documenti">
                  <div class="file-actions">
                    <div
                      class="file-dropzone"
                      :class="{
                        'is-dragover': isDropZoneActive(row.id, row.latestProgram.id),
                        'is-uploading': isUploading(row.id, row.latestProgram.id),
                        'is-disabled': !canEdit || isUploading(row.id, row.latestProgram.id),
                        'has-preview': !!getDropPreview(row.id, row.latestProgram.id)
                      }"
                      :aria-busy="isUploading(row.id, row.latestProgram.id)"
                      aria-live="polite"
                      role="button"
                      tabindex="0"
                      @dragenter.prevent="handleDropZoneEnter(row.id, row.latestProgram.id)"
                      @dragover.prevent="handleDropZoneEnter(row.id, row.latestProgram.id)"
                      @dragleave.prevent="handleDropZoneLeave(row.id, row.latestProgram.id)"
                      @drop.prevent="handleFileDrop(row.uid || row.id, row.id, row.latestProgram.id, $event)"
                      @click.stop="handleDropZoneClick(row.uid || row.id, row.id, row.latestProgram.id)"
                      @keydown.enter.stop.prevent="handleDropZoneClick(row.uid || row.id, row.id, row.latestProgram.id)"
                      @keydown.space.stop.prevent="handleDropZoneClick(row.uid || row.id, row.id, row.latestProgram.id)"
                    >
                      <template v-if="isUploading(row.id, row.latestProgram.id)">
                        <span class="file-dropzone__spinner" aria-hidden="true"></span>
                        <span>Caricamento in corso...</span>
                      </template>
                      <template v-else>
                        <span class="material-icons file-dropzone__icon" aria-hidden="true">picture_as_pdf</span>
                        <span class="file-dropzone__prompt">
                          {{ getDropPreview(row.id, row.latestProgram.id) || "Clicca o trascina qui il PDF" }}
                        </span>
                      </template>
                    </div>
                    <div v-if="row.latestProgram.fileUrl" class="file-info">
                      <a class="file-link" :href="row.latestProgram.fileUrl" target="_blank" rel="noopener">
                        Visualizza
                      </a>
                      <span v-if="row.latestProgram.dataFileCaricato" class="file-date">
                        {{ formatDate(row.latestProgram.dataFileCaricato) }}
                      </span>
                    </div>
                    <div
                      v-if="getUploadMessage(row.id, row.latestProgram.id)"
                      :class="[
                        'file-status',
                        `file-status--${getUploadMessage(row.id, row.latestProgram.id).type || 'info'}`
                      ]"
                      aria-live="polite"
                    >
                      {{ getUploadMessage(row.id, row.latestProgram.id).text }}
                    </div>
                  </div>
                </td>
                <td class="col-consegna">
                  <div class="consegna-box">
                    <div class="consegna-header">
                      <div class="consegna-title">
                        {{ getInvioProgramHeader(row, row.latestProgram) }}
                      </div>
                      <span class="consegna-step-tag">
                        {{ getInvioStepLabel(row.latestProgram) }}
                      </span>
                    </div>
                    <div class="consegna-steps">
                      <label
                        class="consegna-step-item consegna-step-item--custom"
                        :class="{
                          'is-done': isProgCustomEnabled(row, row.latestProgram),
                          'is-disabled': isProgCustomDisabled(row, row.latestProgram)
                        }"
                      >
                        <input
                          type="checkbox"
                          :checked="isProgCustomEnabled(row, row.latestProgram)"
                          :disabled="isProgCustomDisabled(row, row.latestProgram)"
                          @change="toggleProgCustomFlag(row, row.latestProgram, $event.target.checked, $event)"
                        />
                        <span>Prog. customizzato</span>
                      </label>
                      <label
                        class="consegna-step-item consegna-step-item--delivery"
                        :class="{
                          'is-done': isConsegnaConfirmed(row, row.latestProgram),
                          'is-disabled': isConsegnaDisabled(row, row.latestProgram)
                        }"
                      >
                        <input
                          type="checkbox"
                          :checked="isConsegnaConfirmed(row, row.latestProgram)"
                          :disabled="isConsegnaDisabled(row, row.latestProgram)"
                          @change="toggleConsegnaFlag(row, row.latestProgram, $event.target.checked, $event)"
                        />
                        <span>{{ isConsegnaConfirmed(row, row.latestProgram) ? "Consegnato" : "Consegna" }}</span>
                      </label>
                    </div>
                    <small
                      v-if="!hasInvioStepRef(row.latestProgram)"
                      class="consegna-hint consegna-hint--error"
                    >
                      Invio senza step collegato.
                    </small>
                    <small
                      v-else-if="isConsegnaNotAvailable(row.latestProgram)"
                      class="consegna-hint"
                    >
                      Nessuna consegna per questo check finale.
                    </small>
                  </div>
                </td>
                <td>
                  <select
                    class="input input--table"
                    :value="draftValue(row.id, 'nutrizionistadiet', row.latestProgram.nutrizionistadiet)"
                    :disabled="!canEdit || isRowSaving(row.id)"
                    @change="updateDraft(row.id, 'nutrizionistadiet', $event.target.value)"
                  >
                    <option value="">Nessuna</option>
                    <option value="francescadg">Francesca D.G.</option>
                    <option value="celestina">Celestina</option>
                    <option value="celestина">Celestина</option>
                    <option value="erika">Erika</option>
                    <option value="martina">Martina</option>
                    <option value="francesca">Francesca</option>
                  </select>
                </td>
                <td>
                  <select
                    class="input input--table"
                    :value="draftValue(row.id, 'noteselect', row.latestProgram.noteselect)"
                    :disabled="!canEdit || isRowSaving(row.id)"
                    @change="updateDraft(row.id, 'noteselect', $event.target.value)"
                  >
                    <option value="">Nessuna</option>
                    <option value="damodificare">Da modificare</option>
                    <option value="corretta">Corretta</option>
                  </select>
                </td>
                <td>
                  <textarea
                    class="input input--table note-input"
                    :value="draftValue(row.id, 'noteinterna', row.latestProgram.noteinterna)"
                    :disabled="!canEdit || isRowSaving(row.id)"
                    rows="1"
                    placeholder="Nota interna"
                    @input="updateDraft(row.id, 'noteinterna', $event.target.value)"
                  ></textarea>
                </td>
                <td class="save-cell">
                  <div class="buttons-container buttons-container--save">
                    <button
                      type="button"
                      class="save-button"
                      :disabled="!canEdit || isRowSaving(row.id) || !hasDraft(row.id)"
                      title="Salva"
                      aria-label="Salva"
                      @click="saveRow(row)"
                    >
                      <span class="material-icons" aria-hidden="true">
                        {{ isRowSaving(row.id) ? "autorenew" : "save" }}
                      </span>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else-if="!isLoading" class="empty-state muted">
          Nessun invio da modificare trovato.
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SidebarComponent from "../SidebarComponent.vue";
import {
  collection,
  doc,
  documentId,
  getDoc,
  getDocs,
  getFirestore,
  limit,
  orderBy,
  query,
  setDoc,
  startAfter,
  Timestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { setProgCustomFlag, setStepDone, stepKeyFromName } from "@/lib/percorsi";
import { buildLatestProgramMirror } from "@/lib/latestProgramMirror";

const PAGE_SIZE = 80;
const TARGET_NOTE = "damodificare";
const CORRECT_NOTE = "corretta";
const VISIBLE_NOTE_VALUES = [TARGET_NOTE, CORRECT_NOTE];
const ROW_REMOVAL_DELAY_MS = 2000;
const EDITABLE_PROGRAM_FIELDS = [
  "nutrizionistadiet",
  "noteselect",
  "noteinterna",
];
const STUDIO_OPTIONS = [
  "Tivoli Quartier generale",
  "FF Genova",
  "FF Bologna",
  "FF Bari",
  "FF Pescara",
  "FF Firenze",
  "FF Torino",
  "FF Napoli",
  "FF Milano",
  "FF Roma",
  "FF Salerno",
  "FF Cagliari",
  "FF Milano Citta Studi",
  "FF Roma Eur",
  "FF Milano Sempione",
  "FF Padova",
];

function parseDateInput(value, endOfDay = false) {
  if (!value) return null;
  const [year, month, day] = String(value).split("-").map(Number);
  if (!year || !month || !day) return null;

  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }

  if (endOfDay) {
    date.setHours(23, 59, 59, 999);
  } else {
    date.setHours(0, 0, 0, 0);
  }

  return date;
}

function dateToTimestamp(value, endOfDay = false) {
  const date = parseDateInput(value, endOfDay);
  if (!date) return null;
  return Timestamp.fromDate(date);
}

function toMillis(value) {
  if (!value) return 0;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.toDate === "function") return value.toDate().getTime();
  if (value instanceof Date) return value.getTime();

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
}

function cloneProgram(program) {
  return program ? { ...program } : null;
}

function findProgramById(programs = [], programId) {
  if (!programId || !Array.isArray(programs)) return null;
  return programs.find((program) => program?.id === programId) || null;
}

function mergePrograms(...programLists) {
  const programMap = new Map();

  programLists.forEach((programs) => {
    if (!Array.isArray(programs)) return;
    programs.forEach((program) => {
      if (!program?.id) return;
      const existing = programMap.get(program.id) || {};
      programMap.set(program.id, { ...existing, ...program });
    });
  });

  return Array.from(programMap.values()).sort((a, b) =>
    toMillis(a?.dataCreazione) - toMillis(b?.dataCreazione)
  );
}

function getLatestProgram(programs = []) {
  const sortedPrograms = Array.isArray(programs)
    ? programs.map(cloneProgram).filter(Boolean)
    : [];

  return sortedPrograms
    .sort((a, b) => toMillis(a?.dataCreazione) - toMillis(b?.dataCreazione))
    .pop() || null;
}

function mergeProgramsForDisplay(inviiPrograms = [], linkedPrograms = []) {
  const mergedPrograms = mergePrograms(inviiPrograms, linkedPrograms);

  return mergedPrograms.map((program) => {
    if (!program?.id) return program;

    const inviiProgram = findProgramById(inviiPrograms, program.id);
    if (!inviiProgram) return program;

    const nextProgram = { ...program };
    EDITABLE_PROGRAM_FIELDS.forEach((field) => {
      if (Object.prototype.hasOwnProperty.call(inviiProgram, field)) {
        nextProgram[field] = inviiProgram[field];
      }
    });
    return nextProgram;
  });
}

function normalizeDraftPatch(draft = {}) {
  return Object.fromEntries(
    Object.entries(draft)
      .filter(([, value]) => value !== undefined)
      .map(([key, value]) => [key, value === "" ? null : value])
  );
}

function patchProgramList(programs = [], programId, patch = {}, fallbackProgram = null) {
  const sourcePrograms = Array.isArray(programs)
    ? programs.map(cloneProgram).filter(Boolean)
    : [];
  let programFound = false;

  const patchedPrograms = sourcePrograms.map((program) => {
    if (program.id !== programId) return program;
    programFound = true;
    return {
      ...program,
      ...patch,
    };
  });

  if (!programFound && fallbackProgram?.id === programId) {
    programFound = true;
    patchedPrograms.push({
      ...fallbackProgram,
      ...patch,
    });
  }

  return {
    programFound,
    programs: patchedPrograms.sort((a, b) =>
      toMillis(a?.dataCreazione) - toMillis(b?.dataCreazione)
    ),
  };
}

export default {
  name: "InviiDaModificareComponent",
  components: {
    SidebarComponent,
  },
  data() {
    return {
      rows: [],
      drafts: {},
      savingRows: {},
      uploadingStates: {},
      pendingRemovalRows: {},
      removalTimeouts: {},
      activeDropZones: {},
      uploadMessages: {},
      uploadMessageTimeouts: {},
      dropPreviews: {},
      userProgramCache: {},
      userProgramLoading: {},
      userProgramLoadPromises: {},
      userProgramErrors: {},
      consegnaSavingState: {},
      progCustomSavingState: {},
      isLoading: true,
      hasMorePages: false,
      inspectedRowsCount: 0,
      lastVisibleDoc: null,
      errorMessage: "",
      successMessage: "",
      filtersOpen: false,
      currentUserId: "",
      currentUserRole: "",
      canEdit: false,
      studioOptions: STUDIO_OPTIONS,
      filters: {
        deliveryStartDate: "",
        deliveryEndDate: "",
        studio: "",
        email: "",
        status: "",
      },
    };
  },
  computed: {
    filteredRows() {
      const email = this.filters.email.trim().toLowerCase();
      const studio = this.filters.studio;
      const status = this.filters.status;
      const startTimestamp = dateToTimestamp(this.filters.deliveryStartDate);
      const endTimestamp = dateToTimestamp(this.filters.deliveryEndDate, true);
      const startMillis = toMillis(startTimestamp);
      const endMillis = toMillis(endTimestamp);

      return this.rows.filter((row) => {
        if (studio && row.studio !== studio) return false;
        if (email && !(row.email || "").toLowerCase().includes(email)) return false;
        if (startTimestamp || endTimestamp) {
          const deliveryMillis = toMillis(row.dataConsegna);
          if (!deliveryMillis) return false;
          if (startTimestamp && deliveryMillis < startMillis) return false;
          if (endTimestamp && deliveryMillis > endMillis) return false;
        }
        if (!this.isRowVisible(row)) return false;
        if (status === "toDeliver" && !this.isRowToDeliver(row)) return false;
        if (status === "toModify" && !this.isRowToModify(row)) return false;
        return true;
      });
    },
  },
  mounted() {
    this.initAuth();
  },
  beforeUnmount() {
    Object.values(this.uploadMessageTimeouts).forEach((timeoutId) => {
      clearTimeout(timeoutId);
    });
    this.clearRowRemovalTimers();
  },
  methods: {
    normalizeNoteValue(value) {
      return String(value || "").trim().toLowerCase();
    },

    isProgramConsideredDelivered(program) {
      if (!program) return false;
      if (program.consegnaConfirmed) return true;
      if (this.isConsegnaNotAvailable(program)) return true;
      return !this.hasInvioStepRef(program);
    },

    isProgramVisibleForPage(program) {
      const note = this.normalizeNoteValue(program?.noteselect);
      if (this.isProgramConsideredDelivered(program)) return false;
      return note === TARGET_NOTE || note === CORRECT_NOTE;
    },

    isRowVisible(row) {
      if (row?.id && this.isRowPendingRemoval(row.id)) return true;
      return this.isProgramVisibleForPage(row?.latestProgram);
    },

    isRowToDeliver(row) {
      if (row?.id && this.isRowPendingRemoval(row.id)) return true;
      const program = row?.latestProgram;
      return (
        this.normalizeNoteValue(program?.noteselect) === CORRECT_NOTE &&
        !this.isProgramConsideredDelivered(program)
      );
    },

    isRowToModify(row) {
      return this.normalizeNoteValue(row?.latestProgram?.noteselect) === TARGET_NOTE;
    },

    isRowSaving(rowId) {
      return !!this.savingRows?.[rowId];
    },

    isRowPendingRemoval(rowId) {
      return !!this.pendingRemovalRows?.[rowId];
    },

    clearRowRemovalTimers() {
      Object.values(this.removalTimeouts).forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });
      this.removalTimeouts = {};
      this.pendingRemovalRows = {};
    },

    cancelScheduledRowRemoval(rowId) {
      if (!rowId) return;
      const timeoutId = this.removalTimeouts[rowId];
      if (timeoutId) clearTimeout(timeoutId);

      const nextTimeouts = { ...(this.removalTimeouts || {}) };
      delete nextTimeouts[rowId];
      this.removalTimeouts = nextTimeouts;

      const nextPending = { ...(this.pendingRemovalRows || {}) };
      delete nextPending[rowId];
      this.pendingRemovalRows = nextPending;
    },

    removeRowFromList(rowId) {
      if (!rowId) return;
      this.rows = this.rows.filter((item) => item.id !== rowId);

      const nextDrafts = { ...(this.drafts || {}) };
      delete nextDrafts[rowId];
      this.drafts = nextDrafts;

      const nextSavingRows = { ...(this.savingRows || {}) };
      delete nextSavingRows[rowId];
      this.savingRows = nextSavingRows;

      const nextPending = { ...(this.pendingRemovalRows || {}) };
      delete nextPending[rowId];
      this.pendingRemovalRows = nextPending;

      const nextTimeouts = { ...(this.removalTimeouts || {}) };
      delete nextTimeouts[rowId];
      this.removalTimeouts = nextTimeouts;
    },

    scheduleDeliveredRowRemoval(rowId) {
      if (!rowId) return;
      this.cancelScheduledRowRemoval(rowId);
      this.pendingRemovalRows = {
        ...(this.pendingRemovalRows || {}),
        [rowId]: true,
      };
      const timeoutId = setTimeout(() => {
        this.removeRowFromList(rowId);
      }, ROW_REMOVAL_DELAY_MS);
      this.removalTimeouts = {
        ...(this.removalTimeouts || {}),
        [rowId]: timeoutId,
      };
    },

    initAuth() {
      const auth = getAuth();
      auth.onAuthStateChanged(async (currentUser) => {
        if (!currentUser) {
          this.isLoading = false;
          this.errorMessage = "Utente non autenticato.";
          return;
        }

        this.currentUserId = currentUser.uid;
        await this.loadCurrentUserRole();
        await this.reloadRows();
      });
    },

    async loadCurrentUserRole() {
      try {
        const db = getFirestore();
        const userSnap = await getDoc(doc(db, "users", this.currentUserId));
        if (!userSnap.exists()) return;

        this.currentUserRole = userSnap.data()?.userRole || "";
        this.canEdit = ["Amministratore", "Studio"].includes(this.currentUserRole);
      } catch (error) {
        console.error("Errore lettura ruolo utente:", error);
        this.canEdit = false;
      }
    },

    buildQuery({ append = false } = {}) {
      const db = getFirestore();
      const constraints = [
        where("latestProgramNoteselect", "in", VISIBLE_NOTE_VALUES),
        orderBy(documentId(), "asc"),
      ];

      if (append && this.lastVisibleDoc) {
        constraints.push(startAfter(this.lastVisibleDoc));
      }

      constraints.push(limit(PAGE_SIZE));

      return query(collection(db, "inviidigiornata"), ...constraints);
    },

    async reloadRows() {
      this.isLoading = true;
      this.errorMessage = "";
      this.successMessage = "";
      this.rows = [];
      this.drafts = {};
      this.clearRowRemovalTimers();
      this.lastVisibleDoc = null;
      this.hasMorePages = false;
      this.inspectedRowsCount = 0;

      try {
        await this.fetchAllRows();
      } catch (error) {
        console.error("Errore caricamento invii da modificare:", error);
        this.errorMessage = "Errore durante il caricamento degli invii.";
      } finally {
        this.isLoading = false;
      }
    },

    async fetchAllRows() {
      let append = false;
      let hasNextPage = false;

      do {
        hasNextPage = await this.fetchRows({ append });
        append = true;
      } while (hasNextPage);

      this.rows = this.sortRows(this.rows);
      this.hasMorePages = false;
    },

    async fetchRows({ append = false } = {}) {
      const snapshot = await getDocs(this.buildQuery({ append }));
      const normalizedRows = (
        await Promise.all(snapshot.docs.map((docSnap) => this.normalizeRow(docSnap)))
      ).filter(Boolean);

      this.rows = append ? [...this.rows, ...normalizedRows] : normalizedRows;
      this.inspectedRowsCount += snapshot.docs.length;
      this.lastVisibleDoc = snapshot.docs[snapshot.docs.length - 1] || null;
      this.hasMorePages = snapshot.docs.length === PAGE_SIZE;

      return this.hasMorePages;
    },

    sortRows(rows) {
      return rows.slice().sort((a, b) => {
        const deliveryDiff = toMillis(a?.dataConsegna) - toMillis(b?.dataConsegna);
        if (deliveryDiff !== 0) return deliveryDiff;
        return toMillis(a?.latestProgram?.dataCreazione) - toMillis(b?.latestProgram?.dataCreazione);
      });
    },

    async normalizeRow(docSnap) {
      const data = docSnap.data() || {};
      const alimentazioni = Array.isArray(data.alimentazioni)
        ? data.alimentazioni.map(cloneProgram).filter(Boolean)
        : [];
      const sortedPrograms = alimentazioni.sort((a, b) =>
        toMillis(a?.dataCreazione) - toMillis(b?.dataCreazione)
      );
      const latestProgram = sortedPrograms[sortedPrograms.length - 1] || null;

      if (!this.isProgramVisibleForPage(latestProgram)) {
        return null;
      }

      const row = {
        ...data,
        id: docSnap.id,
        alimentazioni: sortedPrograms,
        latestProgram,
      };

      const enrichedRow = await this.enrichRowFromAlimentazioni(row);
      return this.isRowVisible(enrichedRow) ? enrichedRow : null;
    },

    async enrichRowFromAlimentazioni(row) {
      const userUid = row?.uid || row?.id;
      if (!userUid || !row?.latestProgram?.id) return row;

      try {
        const db = getFirestore();
        const alimentazioniSnap = await getDoc(doc(db, "alimentazioni", userUid));
        if (!alimentazioniSnap.exists()) return row;

        const linkedPrograms = Array.isArray(alimentazioniSnap.data()?.alimentazioni)
          ? alimentazioniSnap.data().alimentazioni.map(cloneProgram).filter(Boolean)
          : [];
        const linkedLatestProgram = findProgramById(linkedPrograms, row.latestProgram.id);
        if (!linkedLatestProgram) return row;

        const mergedPrograms = mergeProgramsForDisplay(row.alimentazioni, linkedPrograms);
        const latestProgram = findProgramById(mergedPrograms, row.latestProgram.id) || {
          ...row.latestProgram,
          ...linkedLatestProgram,
        };

        return {
          ...row,
          alimentazioni: mergedPrograms,
          latestProgram,
        };
      } catch (error) {
        console.error("Errore lettura nota da alimentazioni:", error);
        return row;
      }
    },

    async ensureUserProgramsLoaded(userUid) {
      if (!userUid) return;
      if (Array.isArray(this.userProgramCache[userUid])) return;
      if (this.userProgramLoadPromises[userUid]) {
        await this.userProgramLoadPromises[userUid];
        return;
      }

      this.userProgramLoading = {
        ...this.userProgramLoading,
        [userUid]: true,
      };

      const loadPromise = (async () => {
        try {
          const db = getFirestore();
          const percorsiQuery = query(collection(db, "percorsi"), where("uid", "==", userUid));
          const snapshot = await getDocs(percorsiQuery);
          const normalized = snapshot.docs
            .map((docSnap) => this.normalizePercorsoSnapshot(docSnap))
            .filter(Boolean);

          this.userProgramCache = {
            ...this.userProgramCache,
            [userUid]: normalized,
          };

          if (this.userProgramErrors[userUid]) {
            const nextErrors = { ...this.userProgramErrors };
            delete nextErrors[userUid];
            this.userProgramErrors = nextErrors;
          }
        } catch (error) {
          console.error(`Errore caricamento programmi per ${userUid}:`, error);
          this.userProgramErrors = {
            ...this.userProgramErrors,
            [userUid]: "Impossibile caricare i programmi.",
          };
        } finally {
          const nextLoading = { ...this.userProgramLoading };
          delete nextLoading[userUid];
          this.userProgramLoading = nextLoading;

          const nextPromises = { ...this.userProgramLoadPromises };
          delete nextPromises[userUid];
          this.userProgramLoadPromises = nextPromises;
        }
      })();

      this.userProgramLoadPromises = {
        ...this.userProgramLoadPromises,
        [userUid]: loadPromise,
      };

      await loadPromise;
    },

    normalizePercorsoSnapshot(docSnap) {
      if (!docSnap) return null;
      const data = docSnap.data ? docSnap.data() || {} : {};
      const steps = Array.isArray(data.steps)
        ? data.steps.map((step) => ({ ...step }))
        : [];
      const consegnaIndex = steps.findIndex((step) => stepKeyFromName(step?.name) === "consegna");

      return {
        id: docSnap.id,
        title: data.title || "",
        productName: data.productName || "",
        sequenceGroupId: data.sequenceGroupId || null,
        sequenceIndex: data.sequenceIndex ?? null,
        sequenceTotal: data.sequenceTotal ?? null,
        consulenzeOnly: !!data.consulenzeOnly,
        steps,
        consegnaIndex,
        consegnaStep: consegnaIndex >= 0 ? { ...steps[consegnaIndex] } : null,
      };
    },

    updateRowsWithPrograms(rowId, programs, targetProgramId = null) {
      const safePrograms = Array.isArray(programs)
        ? programs.map(cloneProgram).filter(Boolean)
        : [];
      const latestProgram =
        (targetProgramId && findProgramById(safePrograms, targetProgramId)) ||
        getLatestProgram(safePrograms);

      this.rows = this.rows.map((item) => {
        if (item.id !== rowId) return item;
        return {
          ...item,
          alimentazioni: safePrograms,
          latestProgram,
        };
      });
    },

    async persistProgramPatch(row, programId, patch = {}) {
      if (!row?.id || !programId) {
        throw new Error("Riga o programma non valido.");
      }

      const cleanPatch = Object.fromEntries(
        Object.entries(patch).filter(([, value]) => value !== undefined)
      );
      if (!Object.keys(cleanPatch).length) {
        return row.latestProgram;
      }

      const db = getFirestore();
      const userUid = row.uid || row.id;
      const inviiRef = doc(db, "inviidigiornata", row.id);
      const alimentazioniRef = doc(db, "alimentazioni", userUid);
      const [inviiSnap, alimentazioniSnap] = await Promise.all([
        getDoc(inviiRef),
        getDoc(alimentazioniRef),
      ]);

      if (!inviiSnap.exists()) {
        throw new Error("Documento invii non trovato.");
      }

      const inviiPrograms = Array.isArray(inviiSnap.data()?.alimentazioni)
        ? inviiSnap.data().alimentazioni.map(cloneProgram).filter(Boolean)
        : [];
      const alimentazioniPrograms =
        alimentazioniSnap.exists() && Array.isArray(alimentazioniSnap.data()?.alimentazioni)
          ? alimentazioniSnap.data().alimentazioni.map(cloneProgram).filter(Boolean)
          : [];
      const fallbackProgram =
        findProgramById(mergePrograms(inviiPrograms, alimentazioniPrograms, row.alimentazioni), programId) ||
        row.latestProgram;

      const inviiPatch = patchProgramList(inviiPrograms, programId, cleanPatch, fallbackProgram);
      const alimentazioniPatch = patchProgramList(
        alimentazioniPrograms,
        programId,
        cleanPatch,
        fallbackProgram
      );

      if (!inviiPatch.programFound && !alimentazioniPatch.programFound) {
        throw new Error(`Programma ${programId} non trovato.`);
      }

      await updateDoc(inviiRef, {
        alimentazioni: inviiPatch.programs,
        ...buildLatestProgramMirror(inviiPatch.programs),
      });
      await setDoc(
        alimentazioniRef,
        {
          userId: userUid,
          alimentazioni: alimentazioniPatch.programs,
        },
        { merge: true }
      );

      const displayPrograms = mergeProgramsForDisplay(inviiPatch.programs, alimentazioniPatch.programs);
      const updatedProgram = findProgramById(displayPrograms, programId) || getLatestProgram(displayPrograms);
      this.updateRowsWithPrograms(row.id, displayPrograms, programId);
      return updatedProgram;
    },

    triggerFileUpload(userUid, rowId, programId) {
      if (!this.canEdit || this.isUploading(rowId, programId)) return;
      const fileInput = document.createElement("input");
      fileInput.type = "file";
      fileInput.accept = "application/pdf,image/jpeg,image/png";
      fileInput.onchange = (event) => {
        const file = event.target?.files?.[0];
        if (!file) return;
        this.setDropPreview(rowId, programId, this.formatDropPreviewName(file.name));
        this.uploadProgramFile(userUid, rowId, programId, file);
      };
      fileInput.click();
    },

    formatDropPreviewName(fileName) {
      if (!fileName) return "";
      const trimmed = fileName.trim();
      const maxLength = 36;
      return trimmed.length > maxLength ? `${trimmed.slice(0, maxLength - 3)}...` : trimmed;
    },

    getDropZoneKey(rowId, programId) {
      return `${rowId || "row"}-${programId || "program"}`;
    },

    isDropZoneActive(rowId, programId) {
      return !!this.activeDropZones[this.getDropZoneKey(rowId, programId)];
    },

    setDropZoneState(rowId, programId, isActive) {
      const key = this.getDropZoneKey(rowId, programId);
      const nextState = { ...this.activeDropZones };
      if (isActive) {
        nextState[key] = true;
      } else {
        delete nextState[key];
      }
      this.activeDropZones = nextState;
    },

    getDropPreview(rowId, programId) {
      return this.dropPreviews[this.getDropZoneKey(rowId, programId)] || "";
    },

    setDropPreview(rowId, programId, text) {
      const key = this.getDropZoneKey(rowId, programId);
      const nextPreviews = { ...this.dropPreviews };
      if (text) {
        nextPreviews[key] = text;
      } else {
        delete nextPreviews[key];
      }
      this.dropPreviews = nextPreviews;
    },

    clearDropPreview(rowId, programId) {
      this.setDropPreview(rowId, programId, "");
    },

    getUploadMessage(rowId, programId) {
      return this.uploadMessages[this.getDropZoneKey(rowId, programId)] || null;
    },

    setUploadMessage(rowId, programId, message, options = {}) {
      const key = this.getDropZoneKey(rowId, programId);
      if (!message) {
        this.clearUploadMessage(rowId, programId);
        return;
      }

      this.uploadMessages = {
        ...this.uploadMessages,
        [key]: message,
      };

      const { autoHide = message?.type !== "loading", duration = 4000 } = options;
      if (this.uploadMessageTimeouts[key]) {
        clearTimeout(this.uploadMessageTimeouts[key]);
      }

      if (autoHide) {
        this.uploadMessageTimeouts = {
          ...this.uploadMessageTimeouts,
          [key]: setTimeout(() => {
            this.clearUploadMessage(rowId, programId);
          }, duration),
        };
      }
    },

    clearUploadMessage(rowId, programId) {
      const key = this.getDropZoneKey(rowId, programId);
      const nextMessages = { ...this.uploadMessages };
      delete nextMessages[key];
      this.uploadMessages = nextMessages;

      if (this.uploadMessageTimeouts[key]) {
        clearTimeout(this.uploadMessageTimeouts[key]);
        const nextTimeouts = { ...this.uploadMessageTimeouts };
        delete nextTimeouts[key];
        this.uploadMessageTimeouts = nextTimeouts;
      }
    },

    isUploading(rowId, programId) {
      return !!this.uploadingStates[this.getDropZoneKey(rowId, programId)];
    },

    handleDropZoneEnter(rowId, programId) {
      if (!this.canEdit) return;
      this.setDropZoneState(rowId, programId, true);
      this.setDropPreview(rowId, programId, "Rilascia qui per caricare");
    },

    handleDropZoneLeave(rowId, programId) {
      this.setDropZoneState(rowId, programId, false);
      if (!this.isUploading(rowId, programId)) {
        this.clearDropPreview(rowId, programId);
      }
    },

    handleFileDrop(userUid, rowId, programId, event) {
      if (!this.canEdit || this.isUploading(rowId, programId)) return;
      this.handleDropZoneLeave(rowId, programId);

      const files = event.dataTransfer?.files;
      if (!files || files.length === 0) return;

      const [file] = files;
      if (!file) return;

      this.setDropPreview(rowId, programId, this.formatDropPreviewName(file.name));
      this.uploadProgramFile(userUid, rowId, programId, file);
    },

    handleDropZoneClick(userUid, rowId, programId) {
      if (!this.canEdit || this.isUploading(rowId, programId)) return;
      this.triggerFileUpload(userUid, rowId, programId);
    },

    async uploadProgramFile(userUid, rowId, programId, file) {
      const maxSizeBytes = 10 * 1024 * 1024;
      const lowerName = (file?.name || "").toLowerCase();
      const fileType = file?.type?.includes("pdf") || lowerName.endsWith(".pdf")
        ? "pdf"
        : file?.type?.includes("png") || lowerName.endsWith(".png")
          ? "png"
          : file?.type?.includes("jpeg") || file?.type?.includes("jpg") || lowerName.endsWith(".jpg") || lowerName.endsWith(".jpeg")
            ? "jpg"
            : null;

      if (!userUid || !rowId || !programId) {
        this.setUploadMessage(rowId, programId, {
          text: "Dati mancanti per il caricamento.",
          type: "error",
        });
        return;
      }

      if (!file || file.size > maxSizeBytes) {
        this.setUploadMessage(rowId, programId, {
          text: "Il file selezionato supera i 10 MB.",
          type: "error",
        });
        this.clearDropPreview(rowId, programId);
        return;
      }

      if (!fileType) {
        this.setUploadMessage(rowId, programId, {
          text: "Sono consentiti solo file PDF o immagini JPG/PNG.",
          type: "error",
        });
        this.clearDropPreview(rowId, programId);
        return;
      }

      const key = this.getDropZoneKey(rowId, programId);
      this.uploadingStates = { ...this.uploadingStates, [key]: true };
      this.setDropZoneState(rowId, programId, true);
      this.setUploadMessage(
        rowId,
        programId,
        {
          text: "Caricamento in corso...",
          type: "loading",
        },
        { autoHide: false }
      );

      try {
        const row = this.rows.find((item) => item.id === rowId);
        if (!row) throw new Error("Riga non trovata.");

        const storage = getStorage();
        const safeFileName = `${Date.now()}_${file.name.replace(/[\\/]/g, "_")}`;
        const filePath = `alimentazioni/${userUid}/${programId}/${safeFileName}`;
        const fileRef = storageRef(storage, filePath);
        await uploadBytes(fileRef, file);
        const fileUrl = await getDownloadURL(fileRef);
        const dataFileCaricato = Timestamp.fromDate(new Date());

        await this.persistProgramPatch(row, programId, {
          fileUrl,
          fileType,
          dataFileCaricato,
        });

        this.setDropPreview(rowId, programId, `Caricato: ${this.formatDropPreviewName(file.name)}`);
        this.setUploadMessage(rowId, programId, {
          text: "File caricato con successo.",
          type: "success",
        });
      } catch (error) {
        console.error("Errore caricamento documento programma:", error);
        this.setUploadMessage(rowId, programId, {
          text: "Errore durante il caricamento del file.",
          type: "error",
        });
        this.clearDropPreview(rowId, programId);
      } finally {
        const nextStates = { ...this.uploadingStates };
        delete nextStates[key];
        this.uploadingStates = nextStates;
        this.setDropZoneState(rowId, programId, false);
      }
    },

    getPercorsoById(userUid, percorsoId) {
      if (!userUid || !percorsoId) return null;
      const list = this.userProgramCache[userUid];
      if (!Array.isArray(list)) return null;
      return list.find((percorso) => percorso.id === percorsoId) || null;
    },

    normalizeInvioDeliveryTargetMode(rawMode) {
      return rawMode === "next" || rawMode === "current" ? rawMode : null;
    },

    isLegacyCurrentDeliveryCheck(program) {
      if (!program) return false;
      const key = (program.invioStepKey || "").toLowerCase();
      if (key !== "check_finale") return false;
      const stepIndex = Number(program.invioStepIndex);
      return Number.isFinite(stepIndex) && stepIndex === 0;
    },

    getInvioDeliveryTargetMode(program) {
      if (!program) return "current";
      const key = (program.invioStepKey || "").toLowerCase();
      if (key !== "check_finale") return "current";
      const explicitMode = this.normalizeInvioDeliveryTargetMode(program.invioDeliveryTargetMode);
      if (explicitMode) return explicitMode;
      return this.isLegacyCurrentDeliveryCheck(program) ? "current" : "next";
    },

    getInvioExpectedTargetSequenceIndex(program) {
      if (!program) return null;
      const deliveryMode = this.getInvioDeliveryTargetMode(program);
      const sourceIndex = Number(program.invioStepSequenceIndex);
      const sourceTotal = Number(program.invioStepSequenceTotal);

      if (Number.isFinite(sourceIndex) && sourceIndex > 0) {
        if (
          deliveryMode === "next" &&
          Number.isFinite(sourceTotal) &&
          sourceTotal > 0 &&
          sourceIndex < sourceTotal
        ) {
          return sourceIndex + 1;
        }
        return sourceIndex;
      }

      const linkedIndex = Number(program.percorsoSequenceIndex);
      return Number.isFinite(linkedIndex) && linkedIndex > 0 ? linkedIndex : null;
    },

    getInvioExpectedTargetSequenceTotal(program) {
      if (!program) return null;
      const sourceTotal = Number(program.invioStepSequenceTotal);
      if (Number.isFinite(sourceTotal) && sourceTotal > 0) return sourceTotal;
      const linkedTotal = Number(program.percorsoSequenceTotal);
      return Number.isFinite(linkedTotal) && linkedTotal > 0 ? linkedTotal : null;
    },

    resolveLinkedPercorso(row, program) {
      const userUid = row?.uid || row?.id;
      const percorsoId = program?.percorsoId;
      if (!userUid) return null;

      const list = this.userProgramCache[userUid];
      const linked = percorsoId ? this.getPercorsoById(userUid, percorsoId) : null;
      if (!Array.isArray(list) || !list.length) return linked;

      const expectedIndex = this.getInvioExpectedTargetSequenceIndex(program);
      const expectedTotal = this.getInvioExpectedTargetSequenceTotal(program);
      if (!Number.isFinite(expectedIndex) || expectedIndex <= 0) return linked;

      const candidateFilter = (item) => {
        if (!item || item.consulenzeOnly) return false;
        const idx = Number(item.sequenceIndex);
        const tot = Number(item.sequenceTotal);
        if (!Number.isFinite(idx) || idx !== expectedIndex) return false;
        if (Number.isFinite(expectedTotal) && Number.isFinite(tot) && tot !== expectedTotal) {
          return false;
        }
        return true;
      };

      if (linked && candidateFilter(linked)) return linked;

      const candidates = list.filter(candidateFilter);
      if (linked?.sequenceGroupId) {
        const sameGroup = candidates.filter(
          (item) => (item.sequenceGroupId || null) === (linked.sequenceGroupId || null)
        );
        if (sameGroup.length === 1) return sameGroup[0];
        if (sameGroup.length > 1) return linked;
      }

      if (candidates.length === 1) return candidates[0];
      return linked;
    },

    hasStoredTargetMismatch(program, percorso) {
      if (!program || !percorso) return false;
      const storedId = program.percorsoId || null;
      const resolvedId = percorso.id || null;
      if (storedId !== resolvedId) return true;

      const storedIndex = Number(program.percorsoSequenceIndex);
      const resolvedIndex = Number(percorso.sequenceIndex);
      if (
        Number.isFinite(storedIndex) &&
        storedIndex > 0 &&
        Number.isFinite(resolvedIndex) &&
        resolvedIndex > 0 &&
        storedIndex !== resolvedIndex
      ) {
        return true;
      }

      const storedTotal = Number(program.percorsoSequenceTotal);
      const resolvedTotal = Number(percorso.sequenceTotal);
      return (
        Number.isFinite(storedTotal) &&
        storedTotal > 0 &&
        Number.isFinite(resolvedTotal) &&
        resolvedTotal > 0 &&
        storedTotal !== resolvedTotal
      );
    },

    buildResolvedInvioLinkPatch(program, percorso) {
      if (!program) return null;
      const patch = {};
      const normalizedDeliveryMode = this.getInvioDeliveryTargetMode(program);

      if ((program.invioDeliveryTargetMode || null) !== normalizedDeliveryMode) {
        patch.invioDeliveryTargetMode = normalizedDeliveryMode;
      }

      if (percorso && this.hasStoredTargetMismatch(program, percorso)) {
        patch.percorsoId = percorso.id || null;
        patch.percorsoTitle =
          (percorso.title || percorso.productName || program.percorsoTitle || "").trim() || null;
        patch.percorsoSequenceIndex =
          Number.isFinite(Number(percorso.sequenceIndex)) && Number(percorso.sequenceIndex) > 0
            ? Number(percorso.sequenceIndex)
            : null;
        patch.percorsoSequenceTotal =
          Number.isFinite(Number(percorso.sequenceTotal)) && Number(percorso.sequenceTotal) > 0
            ? Number(percorso.sequenceTotal)
            : null;
      }

      return Object.keys(patch).length ? patch : null;
    },

    isConsegnaConfirmed(row, program) {
      const userUid = row?.uid || row?.id;
      const cachedPrograms = userUid ? this.userProgramCache[userUid] : null;
      const linked = this.resolveLinkedPercorso(row, program);
      if (linked && typeof linked.consegnaIndex === "number" && linked.consegnaIndex >= 0) {
        return !!linked.consegnaStep?.done;
      }
      if (this.isProgramConsideredDelivered(program)) return true;
      if (Array.isArray(cachedPrograms)) return false;
      return !!program?.consegnaConfirmed;
    },

    isProgCustomEnabled(row, program) {
      const userUid = row?.uid || row?.id;
      const cachedPrograms = userUid ? this.userProgramCache[userUid] : null;
      const linked = this.resolveLinkedPercorso(row, program);
      if (linked && typeof linked.consegnaIndex === "number" && linked.consegnaIndex >= 0) {
        return !!linked.consegnaStep?.progCustomizzato;
      }
      if (Array.isArray(cachedPrograms)) return false;
      return !!program?.progCustomizzato;
    },

    hasInvioStepRef(program) {
      if (!program) return false;
      const key = (program.invioStepKey || "").trim();
      const label = (program.invioStepLabel || "").trim();
      return !!key || !!label;
    },

    getInvioProgramName(row, program) {
      const linked = this.resolveLinkedPercorso(row, program);
      if (linked) {
        const fromLinked = (linked.title || linked.productName || "").trim();
        if (fromLinked) return fromLinked;
      }
      const title = (program?.percorsoTitle || "").trim();
      return title || "Programma";
    },

    getInvioProgramHeader(row, program) {
      const name = this.getInvioProgramName(row, program);
      const linked = this.resolveLinkedPercorso(row, program);
      const index = Number(linked?.sequenceIndex ?? program?.percorsoSequenceIndex);
      const total = Number(linked?.sequenceTotal ?? program?.percorsoSequenceTotal);

      if (Number.isFinite(index) && index > 0 && Number.isFinite(total) && total > 0) {
        return `${name} - Percorso ${index}/${total}`;
      }
      if (Number.isFinite(index) && index > 0) {
        return `${name} - Percorso ${index}`;
      }
      return name;
    },

    mapInvioStepLabel(stepKey) {
      const normalized = (stepKey || "").toLowerCase();
      if (!normalized) return "";
      if (normalized === "check_finale") return "check";
      if (normalized === "questionario") return "questionario";
      if (normalized === "feedback_programmi") return "feedback";
      return normalized.replace(/_/g, " ");
    },

    getInvioStepLabel(program) {
      if (!program) return "-";
      const rawLabel = (program.invioStepLabel || "").trim();
      const mappedLabel = rawLabel || this.mapInvioStepLabel(program.invioStepKey);
      const baseLabel = mappedLabel || "-";
      const rawIndex =
        program.invioStepSequenceIndex != null
          ? program.invioStepSequenceIndex
          : program.percorsoSequenceIndex;
      const rawTotal =
        program.invioStepSequenceTotal != null
          ? program.invioStepSequenceTotal
          : program.percorsoSequenceTotal;
      const index = Number(rawIndex);
      const total = Number(rawTotal);

      if (Number.isFinite(index) && index > 0 && Number.isFinite(total) && total > 0) {
        return `${baseLabel} ${index}/${total}`;
      }
      if (Number.isFinite(index) && index > 0) return `${baseLabel} ${index}`;
      return baseLabel || "-";
    },

    isConsegnaNotAvailable(program) {
      if (!program) return true;
      const key = (program.invioStepKey || "").toLowerCase();
      if (key !== "check_finale") return false;
      if (this.getInvioDeliveryTargetMode(program) !== "next") return false;
      const index = Number(program.invioStepSequenceIndex);
      const total = Number(program.invioStepSequenceTotal);
      if (!Number.isFinite(index) || !Number.isFinite(total)) return false;
      return index >= total;
    },

    isResolvedTargetAligned(program, percorso) {
      if (!program || !percorso) return true;
      const expectedSequenceIndex = this.getInvioExpectedTargetSequenceIndex(program);
      if (!Number.isFinite(expectedSequenceIndex) || expectedSequenceIndex <= 0) return true;
      const targetSequenceIndex = Number(percorso.sequenceIndex);
      if (!Number.isFinite(targetSequenceIndex) || targetSequenceIndex <= 0) return true;
      return targetSequenceIndex === expectedSequenceIndex;
    },

    isConsegnaDisabled(row, program) {
      if (!this.canEdit || !row || !program) return true;
      if (this.isRowPendingRemoval(row.id)) return true;
      if (this.isConsegnaNotAvailable(program)) return true;
      const userUid = row.uid || row.id;
      const linked = this.resolveLinkedPercorso(row, program);
      const targetPercorsoId = linked?.id || program.percorsoId;
      if (!targetPercorsoId) return true;
      const list = this.userProgramCache[userUid];
      if (Array.isArray(list) && !list.some((item) => item.id === targetPercorsoId)) return true;
      return this.isConsegnaSaving(userUid, targetPercorsoId);
    },

    isProgCustomDisabled(row, program) {
      if (!this.canEdit || !row || !program) return true;
      const userUid = row.uid || row.id;
      const linked = this.resolveLinkedPercorso(row, program);
      const targetPercorsoId = linked?.id || program.percorsoId;
      if (!targetPercorsoId) return true;
      const list = this.userProgramCache[userUid];
      if (Array.isArray(list) && !list.some((item) => item.id === targetPercorsoId)) return true;
      return this.isProgCustomSaving(userUid, targetPercorsoId);
    },

    getConsegnaSavingKey(userUid, percorsoId) {
      if (!userUid || !percorsoId) return "";
      return `${userUid}__${percorsoId}`;
    },

    isConsegnaSaving(userUid, percorsoId) {
      const key = this.getConsegnaSavingKey(userUid, percorsoId);
      return key ? !!this.consegnaSavingState[key] : false;
    },

    getProgCustomSavingKey(userUid, percorsoId) {
      if (!userUid || !percorsoId) return "";
      return `${userUid}__${percorsoId}`;
    },

    isProgCustomSaving(userUid, percorsoId) {
      const key = this.getProgCustomSavingKey(userUid, percorsoId);
      return key ? !!this.progCustomSavingState[key] : false;
    },

    updateCachedConsegnaState(userUid, percorsoId, wantDone) {
      const list = this.userProgramCache[userUid];
      if (!Array.isArray(list)) return;
      const index = list.findIndex((percorso) => percorso.id === percorsoId);
      if (index === -1) return;

      const target = list[index];
      const stepIndex = target.consegnaIndex;
      const updatedSteps = Array.isArray(target.steps)
        ? target.steps.map((step, idx) => {
          if (idx !== stepIndex) return { ...step };
          return {
            ...step,
            done: wantDone,
            doneAt: wantDone ? new Date().toISOString() : null,
          };
        })
        : [];

      const nextList = [...list];
      nextList.splice(index, 1, {
        ...target,
        steps: updatedSteps,
        consegnaStep: stepIndex >= 0
          ? { ...(updatedSteps[stepIndex] || {}), done: wantDone }
          : target.consegnaStep,
      });

      this.userProgramCache = {
        ...this.userProgramCache,
        [userUid]: nextList,
      };
    },

    updateCachedProgCustomState(userUid, stateByPercorsoId = {}) {
      const list = this.userProgramCache[userUid];
      if (!Array.isArray(list) || !list.length) return;

      const nowIso = new Date().toISOString();
      let changed = false;
      const nextList = list.map((percorso) => {
        const nextState = stateByPercorsoId[percorso.id];
        if (!nextState) return percorso;
        if (typeof percorso.consegnaIndex !== "number" || percorso.consegnaIndex < 0) return percorso;

        const steps = Array.isArray(percorso.steps) ? percorso.steps.map((step) => ({ ...step })) : [];
        if (!steps[percorso.consegnaIndex]) return percorso;

        const currentStep = steps[percorso.consegnaIndex];
        const nextCustom = !!nextState.progCustomizzato;
        const nextCustomAt = nextCustom
          ? (nextState.progCustomizzatoAt || currentStep.progCustomizzatoAt || nowIso)
          : null;

        if (
          !!currentStep.progCustomizzato === nextCustom &&
          (currentStep.progCustomizzatoAt || null) === nextCustomAt
        ) {
          return percorso;
        }

        steps[percorso.consegnaIndex] = {
          ...currentStep,
          progCustomizzato: nextCustom,
          progCustomizzatoAt: nextCustomAt,
        };
        changed = true;
        return {
          ...percorso,
          steps,
          consegnaStep: { ...(steps[percorso.consegnaIndex] || {}) },
        };
      });

      if (!changed) return;
      this.userProgramCache = {
        ...this.userProgramCache,
        [userUid]: nextList,
      };
    },

    async toggleConsegnaFlag(row, program, checked, event) {
      if (!row || !program) return;
      const previousValue = this.isConsegnaConfirmed(row, program);
      const nextValue = !!checked;
      const resetCheckbox = () => {
        if (event?.target) event.target.checked = previousValue;
      };

      if (!this.canEdit || nextValue === previousValue) {
        if (!this.canEdit) resetCheckbox();
        return;
      }
      if (this.isConsegnaNotAvailable(program)) {
        resetCheckbox();
        alert("Nessuna consegna prevista per questo check finale.");
        return;
      }

      const userUid = row.uid || row.id;
      if (!userUid) {
        resetCheckbox();
        alert("UID utente mancante per aggiornare la consegna.");
        return;
      }

      let cacheKey = "";
      try {
        await this.ensureUserProgramsLoaded(userUid);
        const percorso =
          this.resolveLinkedPercorso(row, program) ||
          this.getPercorsoById(userUid, program.percorsoId);
        if (!percorso) {
          resetCheckbox();
          alert("Programma non trovato. Aggiorna la pagina e riprova.");
          return;
        }

        const targetPercorsoId = percorso.id || program.percorsoId;
        if (!targetPercorsoId) {
          resetCheckbox();
          alert("Nessun programma collegato all'invio.");
          return;
        }
        if (!this.isResolvedTargetAligned(program, percorso)) {
          resetCheckbox();
          alert("Invio non allineato al programma collegato.");
          return;
        }
        if (typeof percorso.consegnaIndex !== "number" || percorso.consegnaIndex < 0) {
          resetCheckbox();
          alert("Il programma selezionato non ha lo step di consegna.");
          return;
        }
        if (nextValue && !percorso.consegnaStep?.progCustomizzato) {
          const confirmMessage =
            "Sei sicuro di fare la consegna? Il programma non risulta customizzato.";
          if (typeof window !== "undefined" && !window.confirm(confirmMessage)) {
            resetCheckbox();
            return;
          }
        }

        cacheKey = this.getConsegnaSavingKey(userUid, targetPercorsoId);
        this.consegnaSavingState = {
          ...this.consegnaSavingState,
          [cacheKey]: true,
        };

        const stamp = nextValue ? new Date().toISOString() : null;
        await setStepDone(targetPercorsoId, percorso.consegnaIndex, nextValue);
        this.updateCachedConsegnaState(userUid, targetPercorsoId, nextValue);

        await this.persistProgramPatch(row, program.id, {
          ...(this.buildResolvedInvioLinkPatch(program, percorso) || {}),
          consegnaConfirmed: nextValue,
          consegnaConfirmedAt: stamp,
        });
        if (nextValue) {
          this.scheduleDeliveredRowRemoval(row.id);
        } else {
          this.cancelScheduledRowRemoval(row.id);
        }
        this.successMessage = "Consegna programmi salvata.";
        this.errorMessage = "";
      } catch (error) {
        console.error("Errore salvataggio consegna programma:", error);
        resetCheckbox();
        this.successMessage = "";
        this.errorMessage = "Errore durante il salvataggio della consegna.";
      } finally {
        if (cacheKey) {
          const nextSaving = { ...this.consegnaSavingState };
          delete nextSaving[cacheKey];
          this.consegnaSavingState = nextSaving;
        }
      }
    },

    async toggleProgCustomFlag(row, program, checked, event) {
      if (!row || !program) return;
      const previousValue = this.isProgCustomEnabled(row, program);
      const nextValue = !!checked;
      const resetCheckbox = () => {
        if (event?.target) event.target.checked = previousValue;
      };

      if (!this.canEdit || nextValue === previousValue) {
        if (!this.canEdit) resetCheckbox();
        return;
      }

      const userUid = row.uid || row.id;
      if (!userUid) {
        resetCheckbox();
        alert("UID utente mancante per aggiornare il programma customizzato.");
        return;
      }

      let cacheKey = "";
      try {
        await this.ensureUserProgramsLoaded(userUid);
        const percorso =
          this.resolveLinkedPercorso(row, program) ||
          this.getPercorsoById(userUid, program.percorsoId);
        if (!percorso) {
          resetCheckbox();
          alert("Programma non trovato. Aggiorna la pagina e riprova.");
          return;
        }

        const targetPercorsoId = percorso.id || program.percorsoId;
        if (!targetPercorsoId) {
          resetCheckbox();
          alert("Nessun programma collegato all'invio.");
          return;
        }
        if (!this.isResolvedTargetAligned(program, percorso)) {
          resetCheckbox();
          alert("Invio non allineato al programma collegato.");
          return;
        }

        cacheKey = this.getProgCustomSavingKey(userUid, targetPercorsoId);
        this.progCustomSavingState = {
          ...this.progCustomSavingState,
          [cacheKey]: true,
        };

        const response = await setProgCustomFlag(targetPercorsoId, nextValue);
        const stateByPercorsoId = response?.stateByPercorsoId || {};
        this.updateCachedProgCustomState(userUid, stateByPercorsoId);
        const targetState = stateByPercorsoId[targetPercorsoId] || {
          progCustomizzato: nextValue,
          progCustomizzatoAt: nextValue ? new Date().toISOString() : null,
        };

        await this.persistProgramPatch(row, program.id, {
          ...(this.buildResolvedInvioLinkPatch(program, percorso) || {}),
          progCustomizzato: !!targetState.progCustomizzato,
          progCustomizzatoAt: targetState.progCustomizzatoAt || null,
        });
        this.successMessage = "Programma customizzato salvato.";
        this.errorMessage = "";
      } catch (error) {
        console.error("Errore salvataggio programma customizzato:", error);
        resetCheckbox();
        this.successMessage = "";
        this.errorMessage = "Errore durante il salvataggio del programma customizzato.";
      } finally {
        if (cacheKey) {
          const nextSaving = { ...this.progCustomSavingState };
          delete nextSaving[cacheKey];
          this.progCustomSavingState = nextSaving;
        }
      }
    },

    resetFilters() {
      this.filters = {
        deliveryStartDate: "",
        deliveryEndDate: "",
        studio: "",
        email: "",
        status: "",
      };
      this.reloadRows();
    },

    setStatusFilter(status) {
      this.filters = {
        ...this.filters,
        status: this.filters.status === status ? "" : status,
      };
    },

    draftValue(rowId, field, fallback = "") {
      const rowDraft = this.drafts?.[rowId];
      if (rowDraft && Object.prototype.hasOwnProperty.call(rowDraft, field)) {
        return rowDraft[field] ?? "";
      }
      return fallback ?? "";
    },

    updateDraft(rowId, field, value) {
      this.errorMessage = "";
      this.successMessage = "";
      this.drafts = {
        ...(this.drafts || {}),
        [rowId]: {
          ...(this.drafts?.[rowId] || {}),
          [field]: value,
        },
      };
    },

    hasDraft(rowId) {
      const rowDraft = this.drafts?.[rowId];
      return !!rowDraft && Object.keys(rowDraft).length > 0;
    },

    async saveRow(row) {
      if (!this.canEdit || !row?.latestProgram?.id || !this.hasDraft(row.id)) return;

      this.savingRows = {
        ...(this.savingRows || {}),
        [row.id]: true,
      };

      try {
        const draft = this.drafts?.[row.id] || {};
        const patch = normalizeDraftPatch(draft);
        if (!Object.keys(patch).length) return;

        const userUid = row.uid || row.id;
        const fallbackProgram = findProgramById(
          row.alimentazioni,
          row.latestProgram.id
        ) || row.latestProgram;

        const localPatch = patchProgramList(
          row.alimentazioni,
          row.latestProgram.id,
          patch,
          fallbackProgram
        );
        if (!localPatch.programFound) {
          throw new Error(`Programma ${row.latestProgram.id} non trovato prima del salvataggio.`);
        }

        const functions = getFunctions(undefined, "us-central1");
        const saveInvioDaModificare = httpsCallable(functions, "saveInvioDaModificare");
        await saveInvioDaModificare({
          inviiDocId: row.id,
          userUid,
          programId: row.latestProgram.id,
          patch,
        });

        const updatedDisplayPrograms = localPatch.programs;
        const updatedLatestProgram = findProgramById(updatedDisplayPrograms, row.latestProgram.id);
        if (!this.isProgramVisibleForPage(updatedLatestProgram)) {
          this.removeRowFromList(row.id);
          this.successMessage =
            "Modifiche salvate. La riga non è più visibile in questa pagina.";
        } else {
          this.rows = this.rows.map((item) => {
            if (item.id !== row.id) return item;
            return {
              ...item,
              alimentazioni: updatedDisplayPrograms,
              latestProgram: updatedLatestProgram,
            };
          });
          this.successMessage = "Modifiche salvate.";
        }

        const nextDrafts = { ...(this.drafts || {}) };
        delete nextDrafts[row.id];
        this.drafts = nextDrafts;
      } catch (error) {
        console.error("Errore salvataggio invio da modificare:", error);
        this.successMessage = "";
        this.errorMessage =
          error?.code === "permission-denied"
            ? "Permessi insufficienti per salvare la riga su invii e alimentazioni."
            : "Errore durante il salvataggio della riga.";
      } finally {
        const nextSavingRows = { ...(this.savingRows || {}) };
        delete nextSavingRows[row.id];
        this.savingRows = nextSavingRows;
      }
    },

    getUserDetailsLink(userId) {
      if (!userId || !this.$router) return "#";
      const routeData = this.$router.resolve({
        name: "UserDetails",
        params: { userId },
      });
      return routeData?.href || "#";
    },

    fullName(row) {
      return [row?.name, row?.surname].filter(Boolean).join(" ") || "-";
    },

    formatDate(value) {
      if (!value) return "-";
      const date = typeof value?.toDate === "function" ? value.toDate() : new Date(value);
      if (Number.isNaN(date.getTime())) return "-";
      return date.toLocaleDateString("it-IT", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    },
  },
};
</script>

<style scoped>
.dashboard-layout {
  align-items: stretch;
  background: #ffffff;
  height: auto;
  justify-content: flex-start;
  min-height: 100vh;
  overflow-x: hidden;
  width: 100%;
}

.content-container {
  box-sizing: border-box;
  flex: 1 1 auto;
  max-width: none;
  min-width: 0;
  overflow-x: hidden;
  padding: 92px 24px 32px var(--staff-content-inset, 297px);
  transition: padding-left 0.3s ease, padding-right 0.3s ease;
  width: 100%;
}

.content-container.sidebar-hidden {
  padding-left: 30px;
}

.card-container {
  background: #ffffff;
  box-sizing: border-box;
  margin-top: 0;
  max-width: none;
  min-width: 0;
  padding: 0 0 32px;
  width: 100%;
}

.titolo-card {
  color: #0f172a;
  font-style: normal;
  margin: 0 0 18px;
  text-align: left;
}

.titolo-card h1 {
  color: #0f172a;
  font-size: 2rem;
  line-height: 1.15;
  margin: 0 0 8px;
}

.titolo-card h3 {
  color: #475569;
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.35;
  margin: 0;
}

.page-help-text {
  color: #64748b;
  font-size: 0.92rem;
  line-height: 1.45;
  margin: 10px 0 0;
  max-width: 780px;
}

.page-help-text strong {
  color: #334155;
  font-weight: 700;
}

.page-help-text__warning {
  align-items: center;
  color: #dc2626 !important;
  display: inline-flex;
  gap: 4px;
  font-weight: 800;
}

.page-help-text__warning-icon {
  color: #64748b;
  font-size: 1.05em;
  line-height: 1;
}

.dataset-summary {
  margin: 8px 0 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 0.9rem;
  color: #475569;
}

.card-surface {
  background: #ffffff;
  border-radius: 18px;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.08);
  box-sizing: border-box;
  padding: 18px 20px;
  margin-top: 18px;
  max-width: 100%;
  width: 100%;
}

.toolbar-card {
  margin-top: 24px;
}

.filters-header {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.accordion-toggle {
  flex: 1 1 320px;
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  color: #0f172a;
  cursor: pointer;
  transition: border 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
}

.accordion-toggle:hover {
  background: #fff;
  border-color: #cbd5e1;
  box-shadow: 0 12px 26px rgba(15, 23, 42, 0.08);
}

.accordion-toggle .material-icons {
  font-size: 22px;
  color: #475569;
}

.toggle-copy {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}

.toggle-title {
  font-weight: 700;
  font-size: 0.95rem;
}

.toggle-subtitle {
  font-size: 0.82rem;
  color: #64748b;
}

.accordion-panel {
  margin-top: 14px;
  padding: 14px 12px;
  border-radius: 14px;
  border: 1px solid #f1f5f9;
  background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.6);
}

.accordion-enter-active,
.accordion-leave-active {
  transition: all 0.18s ease;
}

.accordion-enter,
.accordion-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  column-gap: 16px;
  row-gap: 14px;
  align-items: flex-end;
  width: 100%;
}

.toolbar-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1 1 190px;
  min-width: 190px;
}

.toolbar-group--lg {
  flex: 2 1 260px;
}

.toolbar-group--md {
  flex: 1.2 1 220px;
}

.toolbar-group--sm {
  flex: 0 1 200px;
  min-width: 200px;
}

.toolbar-group--status {
  flex: 0 1 280px;
  min-width: 260px;
}

.toolbar-actions {
  display: flex;
  gap: 12px;
  flex: 0 0 auto;
  align-items: center;
  margin-left: auto;
}

.toolbar-label {
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #64748b;
}

.date-range-input {
  min-width: 0;
  width: 100%;
}

.date-range-input--short {
  width: 100% !important;
}

.input {
  box-sizing: border-box;
  min-width: 0;
  width: 100%;
  padding: 10px 14px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  color: #0f172a;
  transition: border 0.2s ease, box-shadow 0.2s ease;
}

.input:focus {
  border-color: #2563eb;
  background: #fff;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15);
  outline: none;
}

.input--compact {
  max-width: none;
  padding: 8px 12px;
}

.status-filter-buttons {
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.status-filter-button {
  align-items: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  color: #334155;
  cursor: pointer;
  display: inline-flex;
  font-size: 0.86rem;
  font-weight: 700;
  justify-content: center;
  min-height: 38px;
  padding: 8px 10px;
  transition: background 0.18s ease, border-color 0.18s ease, color 0.18s ease, box-shadow 0.18s ease;
  white-space: nowrap;
}

.status-filter-button:hover:not(:disabled) {
  background: #ffffff;
  border-color: #cbd5e1;
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.08);
}

.status-filter-button:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.status-filter-button--delivery.is-active {
  background: #dcfce7;
  border-color: #86efac;
  color: #166534;
}

.status-filter-button--edit.is-active {
  background: #dbeafe;
  border-color: #93c5fd;
  color: #1d4ed8;
}

.input--table {
  min-width: 140px;
  min-height: 36px;
  padding: 7px 10px;
}

textarea.input.input--table.note-input {
  box-sizing: border-box !important;
  display: block;
  height: 45px !important;
  line-height: 20px !important;
  margin-bottom: 0 !important;
  margin-top: 0 !important;
  max-height: 45px !important;
  min-height: 45px !important;
  min-width: 220px;
  overflow: hidden !important;
  padding-bottom: 7px !important;
  padding-top: 7px !important;
  resize: none !important;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 38px;
  padding: 10px 16px;
  border-radius: 12px;
  border: 1px solid transparent;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
  background: #f1f5f9;
  color: #1e293b;
  border-color: #e2e8f0;
}

.btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.12);
}

.btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  box-shadow: none;
}

.btn.primary {
  background: linear-gradient(135deg, #2563eb, #4f46e5);
  color: #fff;
}

.btn.ghost {
  background: #f1f5f9;
  color: #1e293b;
  border-color: #e2e8f0;
}

.btn-icon {
  width: 42px;
  padding: 0;
}

.tickets {
  overflow-x: auto;
}

.users-table {
  border-collapse: collapse;
  background: #fff;
  min-width: 100%;
  width: max-content;
}

.users-table th,
.users-table td {
  box-sizing: border-box;
  padding: 8px 8px;
  border-bottom: 1px solid #e2e8f0;
  text-align: left;
  vertical-align: middle;
}

.users-table th {
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
}

.users-table td {
  color: #1e293b;
  font-size: 0.92rem;
  line-height: 1.25;
}

.users-table td strong,
.users-table td small {
  display: block;
}

.users-table td small {
  margin-top: 4px;
  color: #64748b;
  font-size: 0.72rem;
}

.buttons-container {
  display: flex;
  gap: 8px;
}

.buttons-container--save {
  justify-content: center;
  min-width: 56px;
}

.buttons-container button,
.table-icon-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 9px;
  border: 1px solid #dbeafe;
  background: #eff6ff;
  color: #2563eb;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.2s ease;
}

.buttons-container button:hover:not(:disabled),
.table-icon-button:hover {
  background: #dbeafe;
}

.save-cell {
  padding-left: 18px;
}

.buttons-container .save-button {
  width: 34px;
  height: 34px;
  border-color: #bbf7d0;
  background: #dcfce7;
  color: #15803d;
}

.buttons-container .save-button:hover:not(:disabled) {
  background: #bbf7d0;
}

.buttons-container .save-button:disabled {
  border-color: #bbf7d0;
  background: #ecfdf5;
  color: #16a34a;
  opacity: 0.58;
}

.buttons-container button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.buttons-container .material-icons,
.table-icon-button .material-icons {
  font-size: 20px;
}

.empty-state {
  margin-top: 16px;
}

.mono {
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
}

.muted {
  color: #64748b;
}

.error {
  color: #b91c1c;
}

:global(.invii-mod-layout section.empty-state),
:global(.invii-mod-layout .empty-state) {
  background: transparent !important;
  border: 0 !important;
  color: #64748b !important;
  margin-top: 16px !important;
  padding: 0 !important;
  text-align: left !important;
}

.error-box {
  margin: 14px 0;
  border-radius: 14px;
}

.success-box {
  margin: 14px 0;
  border-radius: 14px;
  background: #ecfdf5;
  border: 1px solid #bbf7d0;
  color: #166534;
  padding: 12px 14px;
}

.row-saving {
  opacity: 0.7;
}

.row-delivered-pending-removal > td {
  background: #e5e7eb !important;
  color: #64748b;
  transition: background 0.2s ease, color 0.2s ease;
}

.row-delivered-pending-removal .consegna-box,
.row-delivered-pending-removal .file-dropzone,
.row-delivered-pending-removal .input--table {
  background: #eef0f3;
}

.row-color-red {
  background: #fff0f0;
}

.row-color-blue {
  background: #eef5ff;
}

.row-color-yellow {
  background: #fff9d7;
}

.row-color-green {
  background: #effaf1;
}

.row-color-pink {
  background: #fff0f8;
}

.row-color-protocollo {
  background: #f1edff;
}

.col-documenti {
  max-width: 180px;
  min-width: 180px;
  width: 180px;
}

.col-consegna {
  max-width: 260px;
  min-width: 260px;
  width: 260px;
}

.file-actions {
  align-items: flex-start;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-width: 100%;
  min-width: 0;
  width: 100%;
}

.file-dropzone {
  align-items: center;
  background-color: #fafafa;
  border: 2px dashed #b5b5b5;
  border-radius: 8px;
  box-sizing: border-box;
  color: #555;
  cursor: pointer;
  display: flex;
  font-size: 12px;
  gap: 8px;
  justify-content: flex-start;
  max-width: 100%;
  min-height: 42px;
  min-width: 0;
  overflow: hidden;
  padding: 6px 8px;
  text-align: left;
  transition: border-color 0.2s ease, background-color 0.2s ease, color 0.2s ease;
  width: 100%;
}

.file-dropzone.has-preview {
  background-color: #f0f6ff;
  border-color: #0d6efd;
  color: #0b5ed7;
}

.file-dropzone.is-dragover {
  background-color: #e8f0fe;
  border-color: #4a90e2;
  color: #1a73e8;
}

.file-dropzone.is-uploading {
  background: linear-gradient(135deg, rgba(0, 123, 255, 0.08), rgba(0, 123, 255, 0));
  border-color: #007bff;
  color: #0056b3;
}

.file-dropzone.is-disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.file-dropzone:focus-visible {
  border-color: #1a73e8;
  box-shadow: 0 0 0 3px rgba(26, 115, 232, 0.2);
  outline: none;
}

.file-dropzone__spinner {
  animation: spin 0.8s linear infinite;
  border: 2px solid rgba(0, 86, 179, 0.2);
  border-radius: 50%;
  border-top-color: #0056b3;
  height: 14px;
  width: 14px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.file-dropzone__icon {
  color: currentColor;
  font-size: 16px;
}

.file-dropzone__prompt {
  display: block;
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.file-link {
  color: #2563eb;
  font-weight: 700;
  text-decoration: none;
}

.file-link:hover {
  text-decoration: underline;
}

.file-date {
  color: #64748b;
  font-size: 12px;
}

.file-status {
  background-color: #f1f5f9;
  border-radius: 6px;
  color: #334155;
  font-size: 12px;
  padding: 4px 6px;
}

.file-status--loading {
  background-color: #e8f0fe;
  color: #1a73e8;
}

.file-status--success {
  background-color: #e6f4ea;
  color: #1e8e3e;
}

.file-status--error {
  background-color: #fdecea;
  color: #d93025;
}

.consegna-box {
  background: #f8fafc;
  border: 1px solid #e6ebf2;
  border-radius: 12px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-width: 100%;
  padding: 7px 8px;
}

.consegna-header {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.consegna-title {
  color: #1e293b;
  display: -webkit-box;
  font-size: 12px;
  font-weight: 700;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-height: 1.15;
  overflow: hidden;
}

.consegna-step-tag {
  align-items: center;
  background-color: #eef2f6;
  border-radius: 999px;
  color: #243447;
  display: inline-flex;
  font-size: 10.5px;
  font-weight: 600;
  line-height: 1;
  padding: 2px 6px;
  width: fit-content;
}

.consegna-steps {
  display: grid;
  gap: 4px;
  grid-template-columns: minmax(0, 1.2fr) minmax(102px, 0.95fr);
}

.consegna-step-item {
  align-items: center;
  border: 1px solid transparent;
  border-radius: 8px;
  display: inline-flex;
  font-size: 11px;
  font-weight: 600;
  gap: 5px;
  line-height: 1;
  min-width: 0;
  padding: 5px 6px;
  transition: background-color 0.2s ease, border-color 0.2s ease;
}

.consegna-step-item input {
  flex: 0 0 auto;
  height: 13px;
  width: 13px;
}

.consegna-step-item span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.consegna-step-item--custom {
  background-color: #f1f6ff;
  border-color: #dbe7ff;
  color: #1c3d5f;
}

.consegna-step-item--delivery {
  background-color: #f1fbf4;
  border-color: #d9f3e1;
  color: #1f4a32;
}

.consegna-step-item.is-done {
  background-color: #e0f2ff;
  border-color: #b7d8ff;
}

.consegna-step-item--delivery.is-done {
  background-color: #ddf7e6;
  border-color: #b7e6c8;
}

.consegna-step-item.is-disabled {
  opacity: 0.6;
}

.consegna-hint {
  color: #64748b;
  font-size: 11px;
}

.consegna-hint--error {
  color: #c53030;
}

@media (max-width: 900px) {
  .content-container,
  .content-container.sidebar-hidden {
    padding: 88px 12px 24px var(--staff-content-inset, 12px);
  }

  .card-container {
    padding-bottom: 24px;
  }

  .titolo-card h1 {
    font-size: 1.65rem;
  }

  .toolbar,
  .toolbar-actions {
    align-items: stretch;
  }

  .toolbar-actions {
    margin-left: 0;
    width: 100%;
  }

  .toolbar-actions .btn {
    flex: 1 1 auto;
  }

  .toolbar-group,
  .toolbar-group--lg,
  .toolbar-group--md,
  .toolbar-group--sm {
    flex-basis: auto;
    width: 100%;
  }
}
</style>
